import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { FormattedMessage, injectIntl } from 'react-intl';
import Spinner from 'react-spinner';
import { Scrollbars } from 'react-custom-scrollbars';

import { getUsers, editUser, getAdmins, activateUser } from '../../actions/userActions';
import { getAreas, addArea } from '../../actions/areaActions';
import { getDevices } from '../../actions/deviceActions';
import SingleDevice from '../../components/SingleDevice/SingleDevice';
import SingleArea from '../../components/SingleArea/SingleArea';
import AddLogoModal from '../../components/Modal/AddLogoModal';
import ReportsModal from '../../components/Modal/ReportsModal';
import AreaDeviceListModal from '../../components/Modal/AreaDeviceListModal';

/**
 * @typedef {Object} Station
 * @property {number} id
 * @property {number} source
 * @property {boolean} enabled
 * @property {boolean} forecast_enabled
 * @property {[number, number]} coordinates
 * @property {string|null} city
 * @property {string|null} address
 * @property {string} status
 * @property {{}} created_at
 * @property {{}} update_at
 * @property {boolean} publicly_available
 * @property {StationLogo|null} first_logo
 * @property {StationLogo|null} second_logo
 * @property {boolean} send_reports
 */

/**
 * @typedef {Station & {city: string}} StationWithCity
 */

/**
 * @typedef {Object} StationLogo
 * @property {string} filename
 * @property {string} url
 */

/**
 * @typedef {Object} User
 * @property {number} id
 * @property {string} email
 * @property {boolean} enabled
 * @property {string|null} password_requested_at
 * @property {string|null} name
 * @property {boolean} deleted
 * @property {boolean} paying
 */

/**
 * @typedef {Object} Area
 * @property {number} id
 * @property {string} name
 * @property {Station[]} devices
 */

/**
 * @typedef {Object} ComponentProps - Some component props
 * @property {{path: string}} match
 * @property {User[]} users
 * @property {User[]} admins
 * @property {boolean} usersLoading
 * @property {boolean} adminsLoading
 * @property {Station[]} devices
 * @property {boolean} devicesLoading
 * @property {boolean} activateSuccess
 * @property {boolean} activateLoading
 * @property {boolean} activateError
 * @property {Area[]} areas
 * @property {boolean} areasLoading
 * @property {editUser} editUser
 * @property {getUsers} getUsers
 * @property {function} getAdmins
 * @property {getDevices} getDevices
 * @property {function} activateUser
 * @property {function} getAreas
 * @property {function} addArea
 */

/**
 * @typedef {Object} ComponentState
 * @property {string} filter
 * @property {boolean} enabling
 */

/**
 * @extends {React.Component<ComponentProps, ComponentState>}
 * @property {User[]} users
 */
class Dashboard extends Component {
    /**
     * @inheritdoc
     * @param {ComponentProps} props
     */
    constructor(props) {
        super(props);

        this.state = {
            filter: '',
            enabling: false
        };

        /** @type {User[]} */
        this.users = []
    }

    /**
     * Get users depending on current path
     * @param {string} currentPath
     * @return {void}
     */
    getUsers(currentPath) {
        if (currentPath === '/admin/users/admins') {
            return this.props.getAdmins();
        }

        this.props.getUsers();
    }

    /**
     * @inheritdoc
     */
    componentDidMount() {
        this.props.getUsers();
        this.props.getAdmins();
        this.props.getDevices();
        this.props.getAreas();
    }

    /**
     * @inheritdoc
     * @param {ComponentProps} nextProps
     * @param {ComponentState} nextState
     */
    componentDidUpdate(nextProps, nextState) {
        if (nextState.enabling !== this.state.enabling) {
            const currentPath = this.props.match.path;
            this.getUsers(currentPath);
        }
    }

    /**
     * @inheritdoc
     * @param {ComponentProps} nextProps
     */
    componentWillReceiveProps(nextProps) {
        if (this.props.match.path !== nextProps.match.path) {
            const currentPath = nextProps.match.path;
            this.getUsers(currentPath);
            this.props.getDevices();

            if (nextProps.match.path === '/admin/areas')
                this.props.getAreas();
        }
    }

    /**
     * Set users depending on path
     * @return {void}
     */
    setUsers() {
        const currentPath = this.props.match.path;

        if (currentPath === '/admin/users/admins') {
            this.users = this.props.admins;
        }

        if (currentPath === '/admin/users') {
            this.users = this.props.users;
        }
    }

    /**
     * Render stations
     * @return {JSX.Element[]}
     */
    renderDevices() {
        return this.props.devices
            .filter(device => {
                return device.hasOwnProperty('city');
            })
            .filter(
                /**
                 * Validate
                 * @param {Station | StationWithCity} device
                 * @return {device is StationWithCity}
                 */
                device => {
                    return (
                        device.city !== null &&
                        device.coordinates[0] !== 0 &&
                        device.coordinates[1] !== 0
                    );
            })
            .filter(device => {
                return (
                    // Filter by city
                    device.city.toLowerCase().includes(this.state.filter.toLowerCase()) ||
                    // Filter by address if available
                    (device.address && device.address.toLowerCase().includes(this.state.filter.toLowerCase())) ||
                    // Filter by ID
                    device.id === parseInt(this.state.filter, 10)
                );
            })
            .map(device => {
                return (
                    <SingleDevice key={device.id}
                        deviceCity={device.city}
                        deviceAddress={device.address}
                        deviceId={device.id}
                        deviceEnabled={device.enabled}
                        deviceForecastEnabled={device.forecast_enabled}
                        deviceAvailable={device.publicly_available}
                        firstLogo={device.first_logo}
                        secondLogo={device.second_logo}
                        sendReports={device.send_reports}
                    />
                );
            });
    }

    /**
     * Render areas
     * @return {React.ReactElement[]}
     */
    renderAreas() {
        return this.props.areas
            .map(area => {
                const deviceId = area.devices[0] ? area.devices[0].id : null;

                return (
                    <SingleArea
                        key={area.id}
                        areaId={area.id}
                        areaName={area.name}
                        deviceId={deviceId}
                    />
                );
            });
    }

    /**
     * Add area from form
     * @return {void}
     */
    addArea = () => {
        this.props.addArea({name: "Nazwa grupy"}).then(() => this.props.getAreas());
    }

    /**
     * Render areas table
     * @return {JSX.Element}
     */
    renderAreasTable() {
        return (
            <div>
                <button
                    className="btn btn-primary float-right mb-2"
                    type="button"
                    onClick={this.addArea}
                    >
                    <FormattedMessage id="dashboard.buttons.area.add"/>
                </button>
                <table className="table table-hover table-outline mb-0 hidden-sm-down">
                    <thead className="thead-default">
                        <tr>
                            <th className="table__header-small">ID</th>
                            <th className="table__header-medium">
                                Nazwa grupy
                            </th>
                            <th className="table__header-medium">
                                Lista czujników
                            </th>
                            <th className="table__header-medium">
                                Kod do mapy
                            </th>
                            <th className="table__header-medium">
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.renderAreas()}
                    </tbody>
                </table>
            </div>
        )
    }

    /**
     * Render users
     * @return {JSX.Element[]|undefined}
     */
    renderUsers() {
        if (!this.props.users || !this.props.admins) {
            return;
        }

        this.setUsers();

        return this.users
            .filter(user => {
                if (!user.name) {
                    return (user.email).toLowerCase().includes(this.state.filter);
                };

                return (user.name).toLowerCase().includes(this.state.filter);
            })
            .map(user => {
                const displayUser = user.name ? user.name : user.email;
                const btnClass = user.enabled ? "btn btn-success btn--fixed-width" : "btn btn-danger btn--fixed-width";

                return (
                    <tr key={user.id}>
                        <td>
                            <div>{displayUser}</div>
                        </td>
                        <td className="text-right">
                            <button
                                type="button"
                                className={btnClass}
                                onClick={() => {this.onActivateUserClick(user.id)}}
                                >
                                    {!user.enabled ? <FormattedMessage id="edit.user.buttons.activate"/> : <FormattedMessage id="edit.user.buttons.deactivate"/>}
                            </button>
                        </td>
                        <td className="text-right">
                            <Link
                                to={`/admin/users/${user.id}`}
                                className="btn btn--fixed-width btn-info"
                                onClick={() => (this.props.editUser(user.name, user.email, user.enabled, user.deleted, user.paying))}
                                >
                                    <FormattedMessage id="dashboard.buttons.edit"/>
                            </Link>
                        </td>
                    </tr>
                );
            });
    }

    /**
     * Handle search element change event
     * @param {{target: HTMLInputElement}} e
     * @return {void}
     */
    onSearch = (e) => {
        this.setState({
            filter: e.target.value
        });
    }

    /**
     * Handle activate user button click
     * @param {number} id
     * @return {void}
     */
    onActivateUserClick = (id) => {
        this.props.activateUser(id).then(() => this.setState({ enabling: !this.state.enabling }));
    }

    /**
     * @inheritdoc
     * @return {JSX.Element}
     */
    render() {
        const currentPath = this.props.match.path;
        const isPathUsers = (currentPath === '/admin/users/admins') || (currentPath === '/admin/users');
        const isPathDevices = currentPath === '/admin/devices';
        const isPathGroups = currentPath === '/admin/areas'
        const loadSpinner = (this.props.usersLoading || this.props.adminsLoading) || this.props.devicesLoading
        || this.props.areasLoading

        return (
            <div className="animated fadeIn">
                {loadSpinner ? <Spinner /> : null}
                <Scrollbars autoHeight autoHeightMin={700}>

                {isPathGroups ? this.renderAreasTable() :
                    <table className="table table-hover table-outline mb-0 hidden-sm-down">
                        <thead className="thead-default">
                            <tr>
                                {isPathDevices && <th className="table__header-small">ID</th>}
                                <th>
                                    {isPathUsers ?
                                        <FormattedMessage id="dashboard.table.header.user" /> :
                                        <FormattedMessage id="dashboard.table.header.device" />}
                                        <form className="filter-form">
                                            <input id="filter-form" className="form-control" type="text" value={this.state.filter} onChange={this.onSearch}/>
                                        </form>
                                </th>
                                {isPathDevices &&
                                <th className="text-center table__header-medium">
                                    Logo
                                </th>}
                                {isPathDevices &&
                                <th className="text-center table__header-medium">
                                    Raportowanie
                                </th>}
                                {isPathDevices &&
                                    <th className="text-center table__header-medium">
                                        Prognozy
                                    </th>
                                }
                                {isPathDevices &&
                                <th className="text-center table__header-medium">
                                </th>}
                                <th className="text-center table__header-medium">
                                </th>
                                <th className="text-center table__header-medium">
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {isPathUsers ? this.renderUsers() : this.renderDevices()}
                        </tbody>
                    </table>
                }
                </Scrollbars>
                <br/>
                <br/>
                <br/>
                <AddLogoModal />
                <ReportsModal />
                <AreaDeviceListModal />
            </div>
        )
    }
}

export default connect(
    (/** @type {{area: any, auth: any, device: any, modals: any, user: any}} */state) => ({
        users: state.user.list.data,
        admins: state.user.admins.data,
        usersLoading: state.user.list.loading,
        adminsLoading: state.user.admins.loading,
        devices: state.device.data.list,
        devicesLoading: state.device.data.loading,
        activateSuccess: state.user.activate.success,
        activateLoading: state.user.activate.loading,
        activateError: state.user.activate.error,
        areas: state.area.data.list,
        areasLoading: state.area.data.loading,
    }),
    ({ editUser, getUsers, getAdmins, getDevices, activateUser, getAreas, addArea })
)(injectIntl(Dashboard));



// WEBPACK FOOTER //
// ./src/views/Dashboard/Dashboard.js